import { enUS, ko, zhCN } from 'date-fns/locale';
import { forwardRef, useRef, useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Trans, useTranslation } from 'react-i18next';
import calendar from '../../assets/calendar.svg';
import theme from '../../common/color/theme';
import {
  CenterLayout,
  FrameLayout,
  HorizontalLayout,
  useResponsivePadding,
  VerticalBorderBox,
  VerticalLayout
} from '../../common/common-layout';
import {
  Body1,
  Body3,
  H3,
  Text4,
  TitleNormal
} from '../../common/common-text';
import BTBox from '../../common/components/BTBox';
import BTButton from '../../common/components/BTButton';
import { BTInput } from '../../common/components/BTInput';
import { BTRadio } from '../../common/components/BTRadio';
import { BTTextArea } from '../../common/components/BTTextArea';
import i18n from '../../common/i18n/i18n';
import { pxToRem } from '../../common/utils/ConvertUtils';
import '../../css/bbpage.css';
import { useDeviceType } from '../../hooks/useDeviceType';
import { customerReportSchema } from '../../schema/customer/CustomerReportSchema';
import { sendCustomerReport } from '../../service/CustomerService';
import { baseStore } from '../../store/BaseStore';
import DatePickerGlobalStyle from '../common/DatePickerGlobalStyle';

const ReportCenterSection1 = () => {
  const { t } = useTranslation();
  const { horizontal: padding } = useResponsivePadding();
  const [requestorName, setRequestorName] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState<Date | null>(null);
  const datePickerRef = useRef<any>(null);
  const [mobileNo, setMobileNo] = useState('');
  const [email, setEmail] = useState('');
  const [title, setTitle] = useState('');
  const [contents, setContents] = useState('');
  const [agree, setAgree] = useState<'agree' | 'disagree' | ''>('agree');
  const { isMobile } = useDeviceType();
  const lang = i18n.language as 'ko' | 'en';

  const maxLength = 1000;

  // 신고 요청 함수
  const handleSendReport = async () => {
    /// 모든 입력값이 유효할 때만 진행
    const validation = customerReportSchema.safeParse({
      requestorName,
      dateOfBirth,
      mobileNo,
      email,
      title,
      contents,
      agree
    });

    if (!validation.success) {
      alert(validation.error.errors[0].message);
      return;
    }

    try {
      baseStore.showLoading();
      const report = {
        requestorName,
        dateOfBirth: dateOfBirth ? dateOfBirth : new Date('1900-01-01'),
        mobileNo,
        email,
        title,
        contents: contents,
        collectInfoAgreeYn: agree === 'agree',
        replyCompletedYn: false,
        createdAt: new Date(),
      };

      await sendCustomerReport(report);
      alert('제출이 완료되었습니다.');

      // 입력 필드 초기화
      setRequestorName('');
      setDateOfBirth(null);
      setMobileNo('');
      setEmail('');
      setTitle('');
      setContents('');
      setAgree('');
      baseStore.hideLoading();
    } catch (error) {
      alert('제출에 실패했습니다. 다시 시도해주세요.');
      console.error('신고 제출 오류:', error);
      baseStore.hideLoading();
    }

  };

  const handleOpenCalendar = () => {
    if (datePickerRef.current) {
      datePickerRef.current.setOpen(true);
    }
  };

  const CustomDateInput = forwardRef<HTMLInputElement, any>(({ value, onClick, placeholder }, ref) => (
    <BTInput
      readOnly
      ref={ref}
      onClick={onClick}
      value={value}
      placeholder={placeholder}
      style={{
        flex: 1,
        width: '511px',
        paddingRight: '50px'
      }}
    />
  ));

  const CustomDateInputMobile = forwardRef<HTMLInputElement, any>(({ value, onClick, placeholder }, ref) => (
    <BTInput
      readOnly
      ref={ref}
      onClick={onClick}
      value={value}
      placeholder={placeholder}
      style={{
        display: 'block',
        width: '100%',
        paddingRight: '50px'
      }}
    />
  ));

  return (
    <VerticalLayout paddingLeft={padding} paddingRight={padding} style={{ background: 'white' }}>
      <BTBox height={isMobile ? 40 : `${pxToRem(100)}`} />
      <H3>Report Center</H3>
      <BTBox height={isMobile ? 8 : `${pxToRem(11)}`} />
      <Body1 style={{ opacity: 0.5 }}>{t('함께 지키는 깨끗한 환경, 당신의 참여로 시작됩니다.')}</Body1>
      <BTBox height={isMobile ? 23 : `${pxToRem(60)}`} />
      <div style={{ opacity: 0.6, width: '100%', height: '1px', backgroundColor: 'black' }} />
      <BTBox height={isMobile ? 23 : `${pxToRem(60)}`} />

      <HorizontalLayout
        style={{
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'stretch' : 'center',
          gap: isMobile ? '8px' : '0',
        }}
      >
        <Body3 style={{ width: isMobile ? '100%' : '300px' }}>
          {t('작성자')}<span style={{ color: theme.colors.BIBI }}>*</span>
        </Body3>
        <BTInput
          style={{ width: isMobile ? '100%' : '511px' }}
          value={requestorName}
          onChange={(e) => setRequestorName(e.target.value)}
        />
      </HorizontalLayout>

      <BTBox height={isMobile ? 16 : pxToRem(40)} />

      {isMobile ? (
        <VerticalLayout>
          <Body3 style={{ width: '300px' }}>{t('생년월일')}</Body3>
          <BTBox height={isMobile ? 8 : 16} />
          <FrameLayout style={{ width: '100%' }}>
            <DatePickerGlobalStyle />
            <div style={{ width: '100%' }}>
              <DatePicker
                ref={datePickerRef}
                selected={dateOfBirth}
                onChange={(date) => setDateOfBirth(date)}
                locale={['ko', 'ko-mobile'].includes(lang) ? ko : ['en', 'en-mobile'].includes(lang) ? enUS : zhCN}
                dateFormat="yyyy/MM/dd"
                placeholderText="YYYY/MM/DD"
                customInput={<CustomDateInputMobile readOnly />}
                popperPlacement="bottom-start"

                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
              />
            </div>
            <img
              src={calendar}
              style={{ width: '21px', height: '20px', position: 'absolute', top: '10px', right: '10px', cursor: 'pointer' }}
              onClick={handleOpenCalendar}
            />
          </FrameLayout>
        </VerticalLayout>
      ) : (
        <HorizontalLayout style={{ alignItems: 'center' }}>
          <Body3 style={{ width: '300px' }}>{t('생년월일')}</Body3>
          <FrameLayout style={{ width: '511px' }}>
            <DatePickerGlobalStyle />
            <div style={{ display: 'flex', flex: 1, width: '511px' }}>
              <DatePicker
                ref={datePickerRef}
                selected={dateOfBirth}
                onChange={(date) => setDateOfBirth(date)}
                locale={['ko', 'ko-mobile'].includes(lang) ? ko : ['en', 'en-mobile'].includes(lang) ? enUS : zhCN}
                dateFormat="yyyy/MM/dd"
                placeholderText="YYYY/MM/DD"
                customInput={<CustomDateInput readOnly />}
                popperPlacement="bottom-start"

                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
              />
            </div>
            <img
              src={calendar}
              style={{ width: pxToRem(34), height: pxToRem(38), position: 'absolute', top: '10px', right: '10px', cursor: 'pointer' }}
              onClick={handleOpenCalendar}
            />
          </FrameLayout>
        </HorizontalLayout>
      )}

      <BTBox height={isMobile ? 16 : pxToRem(40)} />

      <HorizontalLayout
        style={{
          alignItems: isMobile ? 'flex-start' : 'center',
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? '8px' : '0', // 모바일 간격 조정
        }}
      >
        <Body3 style={{ width: isMobile ? '100%' : '300px' }}>
          {t('휴대폰번호')}
        </Body3>
        <BTInput
          style={{ width: isMobile ? '100%' : '511px' }}
          value={mobileNo}
          onChange={(e) => setMobileNo(e.target.value)}
        />
      </HorizontalLayout>

      <BTBox height={isMobile ? 16 : pxToRem(40)} />

      <HorizontalLayout
        style={{
          alignItems: isMobile ? 'flex-start' : 'center',
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? '8px' : '0',
        }}
      >
        <Body3 style={{ width: isMobile ? '100%' : '300px' }}>
          {t('이메일')}<span style={{ color: theme.colors.BIBI }}>*</span>
        </Body3>
        <BTInput
          style={{ width: isMobile ? '100%' : '511px' }}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </HorizontalLayout>

      <BTBox height={isMobile ? 16 : pxToRem(40)} />

      <HorizontalLayout
        style={{
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'flex-start' : 'center',
          gap: isMobile ? '8px' : '0',
        }}
      >
        <Body3 style={{ width: isMobile ? '100%' : '300px' }}>
          {t('제목')}<span style={{ color: theme.colors.BIBI }}>*</span>
        </Body3>
        <BTInput
          style={{ width: isMobile ? '100%' : 'auto', flex: isMobile ? undefined : 1 }}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
      </HorizontalLayout>

      <BTBox height={isMobile ? 16 : pxToRem(40)} />

      <HorizontalLayout
        style={{
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'flex-start' : 'start',
          gap: isMobile ? '8px' : '0',
        }}
      >
        <Body3 style={{ width: isMobile ? '100%' : '300px' }}>
          {t('내용')}<span style={{ color: theme.colors.BIBI }}>*</span>
        </Body3>
        <BTTextArea
          placeholder={t('불편을 제보해주세요.')}
          style={{
            width: isMobile ? '100%' : 'auto', flex: isMobile ? undefined : 1,
            ...isMobile ? { minHeight: '218px' } : {}
          }}
          value={contents}
          onChange={(e) => setContents(e.target.value)}
          maxLength={maxLength}
        />
      </HorizontalLayout>

      <BTBox height={pxToRem(20)} />

      <HorizontalLayout>
        <Text4 style={{
          paddingLeft: isMobile ? '0px' : '300px', color: '#979797',
          ...isMobile ? { fontSize: '11px' } : {}
        }}>
          {t('10자 이상 1000자 이하로 입력해주세요.')}
        </Text4>
        <div style={{ flex: 1 }} />
        <Text4 style={{
          color: '#979797',
          ...isMobile ? { fontSize: '11px' } : {}
        }}>
          ({contents.length}/{maxLength})
        </Text4>
      </HorizontalLayout>

      <BTBox height={isMobile ? 40 : `${pxToRem(90)}`} />

      <Body3>{t('개인정보보호범에 따른 개인정보 수집 동의 동의')}</Body3>

      <BTBox height={isMobile ? 8 : pxToRem(40)} />
      <VerticalBorderBox style={{ width: '100%', padding: isMobile ? '25px' : pxToRem(50) }}>
        <Body3 style={{
          opacity: '0.6',
          fontWeight: 700,
          fontSize: pxToRem(24),
        }}>{t('개인정보의 수집목적/수집항목/이용목적')}</Body3>
        <BTBox height={pxToRem(40)} />
        <HorizontalLayout style={{ alignItems: 'flex-start' }}>
          <TitleNormal fontSize={isMobile ? 14 : 24} style={{
            lineHeight: isMobile ? '22px' : '40px', opacity: '0.6',
            fontWeight: 400,
            fontSize: pxToRem(24),
          }}>1.</TitleNormal>
          <BTBox width={6} />
          <TitleNormal
            fontSize={isMobile ? 14 : 24}
            style={{
              flex: 1,
              lineHeight: isMobile ? '22px' : '40px',
              opacity: '0.6',
              fontWeight: 400,
              fontSize: pxToRem(24),
            }}
          >
            <Trans i18nKey="윤리딜레마 상담은 최소한의 개인정보만을 수집합니다.<br />상담내용 확인 및 상담결과의 회신 서비스 제공을 목적으로 개인정보 수집 이용 동의를 구합니다." />
          </TitleNormal>
        </HorizontalLayout>
        <HorizontalLayout style={{ alignItems: 'flex-start' }}>
          <TitleNormal fontSize={isMobile ? 14 : 24} style={{
            lineHeight: isMobile ? '22px' : '40px', opacity: '0.6',
            fontWeight: 400,
            fontSize: pxToRem(24),
          }}>2.</TitleNormal>
          <BTBox width={6} />
          <TitleNormal
            fontSize={isMobile ? 14 : 24}
            style={{
              flex: 1,
              lineHeight: isMobile ? '22px' : '40px',
              opacity: '0.6',
              fontWeight: 400,
              fontSize: pxToRem(24),
            }}
          >
            <Trans i18nKey="수집하려는 개인정보 항목<br />- 필수: 작성자, 이메일<br />- 선택: 생년월일, 휴대폰번호" />
          </TitleNormal>
        </HorizontalLayout>
        <HorizontalLayout style={{ alignItems: 'flex-start' }}>
          <TitleNormal fontSize={isMobile ? 14 : 24} style={{
            lineHeight: isMobile ? '22px' : '40px', opacity: '0.6',
            fontWeight: 400,
            fontSize: pxToRem(24),
          }}>3.</TitleNormal>
          <BTBox width={6} />
          <TitleNormal
            fontSize={isMobile ? 14 : 24}
            style={{
              flex: 1,
              lineHeight: isMobile ? '22px' : '40px',
              opacity: '0.6',
              fontWeight: 400,
              fontSize: pxToRem(24),
            }}
          >
            {t('개인정보의 보유 및 이용기간은 정보 주체자의 신고 후 5년 입니다.')}
          </TitleNormal>
        </HorizontalLayout>
        <HorizontalLayout style={{ alignItems: 'flex-start' }}>
          <TitleNormal fontSize={isMobile ? 14 : 24} style={{
            lineHeight: isMobile ? '22px' : '40px', opacity: '0.6',
            fontWeight: 400,
            fontSize: pxToRem(24),
          }}>4.</TitleNormal>
          <BTBox width={6} />
          <TitleNormal
            fontSize={isMobile ? 14 : 24}
            style={{
              flex: 1,
              lineHeight: isMobile ? '22px' : '40px',
              opacity: '0.6',
              fontWeight: 400,
              fontSize: pxToRem(24),
            }}
          >
            {t('정보주체는 동의를 거부 할 권리가 있으며 동의를 거부 할 경우 윤리딜레마 상담 이용을 할 수 없습니다.')}
          </TitleNormal>
        </HorizontalLayout>
      </VerticalBorderBox>
      <BTBox height={isMobile ? 20 : pxToRem(111)} />
      <HorizontalLayout style={{ alignItems: 'center' }}>
        <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
          <BTRadio
            name="privacyConsent"
            value="agree"
            checked={agree === 'agree'}
            onChange={() => setAgree('agree')}
          />
          <BTBox width={pxToRem(10)} />
          <Body3>{t('동의')}</Body3>
        </label>

        <BTBox width={isMobile ? 37 : pxToRem(200)} />

        <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
          <BTRadio
            name="privacyConsent"
            value="disagree"
            checked={agree === 'disagree'}
            onChange={() => setAgree('disagree')}
          />
          <BTBox width={pxToRem(10)} />
          <Body3>{t('동의하지 않음')}</Body3>
        </label>
      </HorizontalLayout>

      <BTBox height={isMobile ? 40 : `${pxToRem(82)}`} />
      <VerticalLayout height={1} backgroundColor='#00000033' />
      <BTBox height={isMobile ? 40 : `${pxToRem(90)}`} />
      <CenterLayout>
        <BTButton width={isMobile ? '100%' : `${pxToRem(359)}`} height={isMobile ? '60px' : `${pxToRem(102)}`} color={theme.colors.BIBI} textColor='white'
          sx={{
            fontWeight: 700,
            fontSize: isMobile ? '15px' : `${pxToRem(28)}`,
            '&:hover': {
              boxShadow: 'none',
              textDecoration: 'underline',
              textUnderlineOffset: '2px',
              textDecorationThickness: '2px',
              borderColor: 'transparent', 
            },
          }}
          onClick={handleSendReport} data-analytics-name="접수하기">{t('접수하기')}</BTButton>
      </CenterLayout>
      <BTBox height={300} />
    </VerticalLayout >
  );
};

export default ReportCenterSection1;
